import useLangText from '@shared/hooks/useLangText';
import {
    ProcessFieldBase,
    type ProcessFieldDate as ProcessFieldDateType,
} from '@shared/types/process';

import {
    isAfterCurrentPeriod,
    isBeforeCurrentPeriod,
    isSameOrAfterCurrentPeriod,
    isSameOrBeforeCurrentPeriod,
} from './dateValidation';
import { ProcessFieldDatePickerAiChat } from './ProcessFieldDatePickerAiChat';

import { ShadcnForm } from '@shadcn-features/core';
import { ShadcnFormDatePickerFlowProps } from '@shadcn-features/core/ShadcnForm/fields/ShadcnFormDatePickerFlow';
import { ShadcnFormRule } from '@shadcn-features/core/ShadcnForm/types';
import { useTranslation } from 'react-i18next';

interface Props extends ShadcnFormDatePickerFlowProps {
    field: ProcessFieldBase & ProcessFieldDateType;
    variant?: 'default' | 'ai-chat';
}

export function ProcessFieldDateField({
    field,
    fieldProps,
    variant,
    ...props
}: Props) {
    const { t } = useTranslation();
    const dateType = field.options.dateType;

    const getLangText = useLangText();

    const rules: Record<
        Exclude<ProcessFieldDateType['options']['validation'], null>,
        ShadcnFormRule<string>[0]['validator']
    > = {
        any: () => {
            return true;
        },
        future: (value: string) => {
            if (!isAfterCurrentPeriod(value, dateType, field.options.format)) {
                return t('dateMustBeFuture');
            }
            return true;
        },
        futureOrToday: (value: string) => {
            if (
                !isSameOrAfterCurrentPeriod(
                    value,
                    dateType,
                    field.options.format,
                )
            ) {
                return t('dateMustBeTodayOrFuture');
            }
            return true;
        },
        past: (value) => {
            if (!isBeforeCurrentPeriod(value, dateType, field.options.format)) {
                return t('dateMustBePast');
            }
            return true;
        },
        pastOrToday: (value) => {
            if (
                !isSameOrBeforeCurrentPeriod(
                    value,
                    dateType,
                    field.options.format,
                )
            ) {
                return t('dateMustBeTodayOrPast');
            }
            return true;
        },
    };
    const DatePicker =
        variant === 'ai-chat'
            ? ProcessFieldDatePickerAiChat
            : ShadcnForm.DatePickerFlow;
    return (
        <DatePicker
            fieldProps={fieldProps}
            tooltip={getLangText(field?.tooltip ?? '')}
            {...props}
            format={field.options.format}
            picker={dateType ?? undefined}
            rules={[
                { validator: rules[field.options.validation || 'any'] },
                ...(props?.rules ?? []),
            ]}
        />
    );
}
